import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Sortable, SortableEvent, SortableStopEvent } from '@shopify/draggable';
import {
  RvSortableItem,
  RvSortableService,
} from '../../services/rv-sortable.service';

@Component({
  selector: 'rv-sortable',
  template: `
    <div *ngIf="hasSortableHandles" id="sortable-instructions" class="sr-only">
      Use Space to select an item, then use Up and Down arrow keys to move it.
      Press Space again to drop the item in its new position, or press Escape to
      cancel.
    </div>
    <ng-content></ng-content>
  `,
  styleUrls: ['./rv-sortable.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sortable-list',
    tabindex: '-1',
  },
  providers: [
    {
      provide: RvSortableService,
      useFactory: (component: RvSortableComponent) => component.sortableService,
      deps: [forwardRef(() => RvSortableComponent)],
    },
  ],
})
export class RvSortableComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
  @Input() sortableService: RvSortableService<RvSortableItem>;

  @Output()
  public sortableEvent = new EventEmitter<SortableEvent>();

  private sortable: Sortable;
  public hasSortableHandles = false;

  constructor(
    private el: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.initializeSortable();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['sortableService'] && this.sortableService) {
      this.initializeSortable();
    }
  }

  private initializeSortable() {
    this.sortable?.destroy();
    this.sortable = new Sortable(this.el.nativeElement, {
      handle: '.rv-sortable-handle',
      draggable: '.rv-sortable-item',
      mirror: {
        appendTo: this.sortableService?.config?.appendTo,
        constrainDimensions: true,
        xAxis: false,
        cursorOffsetY: 30,
      },
    });

    this.sortable.on('sortable:stop', (sortableEvent: SortableStopEvent) => {
      setTimeout(() => {
        // Reset drag state and handle drag sort in service if available
        if (this.sortableService) {
          this.sortableService.onDragEnd(sortableEvent);
        }
        // Still emit for backward compatibility
        this.sortableEvent.emit(sortableEvent);
        this.cdr.detectChanges();
        requestAnimationFrame(() => {
          this.sortable?.destroy();
          this.initializeSortable();
        });
      });
    });
  }

  ngAfterViewInit() {
    // Check if there are any rv-sortable-handle elements in the projected content
    this.hasSortableHandles =
      this.el.nativeElement.querySelector('rv-sortable-handle') !== null;
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.sortable && this.sortable.destroy();
  }
}
